/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { computed, action } from '@ember/object';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import isValidEmail from 'cio/utils/validators/email';
import {
  USER_ROLE_ADMIN,
  USER_ROLE_MANAGER,
  USER_ROLE_AUTHOR,
  USER_ROLE_VIEWER,
  UserRoleType,
  USER_ROLES_REDACT_MAP,
  USER_ROLES_REDACT_SUFFIX,
} from 'cio/utils/user-roles';
import EnvironmentModel from 'cio/models/environment';
import UserModel from 'cio/models/user';
import { inject as service } from '@ember/service';
import AccountService from 'cio/services/account';
import { ProductApp } from 'cio/utils/product-app-info';

const Validations = {
  email(value: string) {
    if (!isValidEmail(value.trim())) {
      return 'Please enter a valid email address';
    }

    return true;
  },
};

const getUserRolesForActiveProducts = (activeProducts: ProductApp[]) => {
  let isJourneysActive = activeProducts.includes(ProductApp.JOURNEYS);
  let isPipelinesActive = activeProducts.includes(ProductApp.DATA_PIPELINES);

  return [
    {
      id: USER_ROLE_ADMIN,
      name: 'Administrator',
      description: 'Full account access, including billing, plan information, and team management.',
    },
    {
      id: USER_ROLE_MANAGER,
      name: 'Workspace Manager',
      description: 'Full control of workspaces below. No access to billing, plan information, or team management.',
    },
    isJourneysActive
      ? {
          id: USER_ROLE_AUTHOR,
          name: 'Author',
          description: `Full access for creating, editing, saving campaigns and broadcasts for the workspaces below. No billing, plan information, team management, importing or exporting data.${
            isPipelinesActive
              ? ' No creating or deleting of Data Pipelines Sources and Destinations, no viewing of Source and Destination API credentials.'
              : ''
          }`,
        }
      : null,
    {
      id: USER_ROLE_VIEWER,
      name: 'Viewer',
      description: isJourneysActive
        ? `Read-only access of the workspaces below. No editing, saving, importing or exporting data, team member management or access to billing or plan information.${
            isPipelinesActive
              ? ' No creating or deleting of Data Pipelines Sources and Destinations, no viewing of Source and Destination API credentials.'
              : ''
          }`
        : 'Read-only access of the workspaces below. No creating or deleting of Sources and Destinations, no viewing of API credentials.',
    },
  ].filter((r) => r != null);
};

interface SettingsTeamNewUserFormArgs {
  user: UserModel;
  environments: EnvironmentModel[];
  onSubmit: (_: UserModel) => Promise<void>;
  onCancel: () => void;
}

export default class SettingsTeamNewUserForm extends Component<SettingsTeamNewUserFormArgs> {
  @service account!: AccountService;

  validations!: FormValidations;

  @tracked hasUpdatedWorkspaces = false;

  constructor(owner: unknown, args: SettingsTeamNewUserFormArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'args.user',
    });
  }

  get userRoles() {
    return getUserRolesForActiveProducts(this.account.accessibleProducts);
  }

  get userRole() {
    return this.args.user.role?.replace(USER_ROLES_REDACT_SUFFIX, '');
  }

  @computed(
    'validations.{isTouched,hasErrors}',
    'submitTask.isRunning',
    'hasUpdatedWorkspaces',
    'args.user.hasDirtyAttributes',
  )
  get submitDisabled() {
    return (
      !this.validations.isTouched ||
      this.validations.hasErrors ||
      this.submitTask.isRunning ||
      (!this.args.user.hasDirtyAttributes && !this.hasUpdatedWorkspaces)
    );
  }

  @computed('args.user.accessibleEnvironments.[]')
  get selectedEnvironments() {
    return this.args.user.accessibleEnvironments.toArray();
  }

  @action
  onChangeRole(role: UserRoleType) {
    this.args.user.set('role', role);

    // Admins can access all workspaces
    if (this.args.user.role === USER_ROLE_ADMIN) {
      this.args.user.set('accessibleEnvironments', this.args.environments);
    }
  }

  @action
  onChangeRoleRedact() {
    this.args.user.set('role', USER_ROLES_REDACT_MAP[this.args.user.role]);
  }

  @action
  onChangeAccessibleEnvironments(workspaces: EnvironmentModel[]) {
    this.args.user.set('accessibleEnvironments', workspaces);
    this.hasUpdatedWorkspaces = true;
  }

  @action
  onUserChange(field: keyof UserModel, value: string) {
    this.args.user.set(field, value);
    this.validations.set('isTouched', true);
    this.args.user.errors.clear();
  }

  submitTask = task(async (evt: Event) => {
    evt.preventDefault();

    await this.validations.validateModel();

    // Trim whitespace from email before saving
    this.args.user.email = this.args.user.email.trim();

    // Admins can access all workspaces
    if (this.args.user.role === USER_ROLE_ADMIN) {
      this.args.user.set('accessibleEnvironments', this.args.environments);
    }

    await this.args.onSubmit(this.args.user);
  });
}
